import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { CreateModelData, DatasetCleanupType } from '../../@shared/Models/Models';
import { DataProviderInstance } from '../../@shared/services/DataProvider';
import { GuideStepEnum, NotificationModel, NotificationModelType } from '../../reducers/main/mainModel';
import { setIsShowModelDataModal, setAiModelId, cleanModelData, getAiModelId, getModelData, getTestValue, getSchemaOrdering, setIsDataCleaning, setIsProcessing } from '../../reducers/modelData/modelDataReducer';
import { onThrowAlert, loadOpenedSchema } from '../dataSaga/dataSagaActions';
import { openSchemaDataModalAction, closeModelDataModalAction, saveModelDataModalAction, cleanModelDataModalAction } from './modelDataSagaActions';
import RespondClient from '../../@shared/Models/RespondClient';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';

const initSaga = [
    takeLatest(openSchemaDataModalAction, watchOpening),
    takeLatest(closeModelDataModalAction, watchCloasing),
    takeLatest(cleanModelDataModalAction, watchCleaning),
    takeLatest(saveModelDataModalAction, watchSaving)
]


export default initSaga;

function* watchOpening({ payload: data }: PayloadAction<string>): Generator {
    try {
        yield put(setIsShowModelDataModal(true));
        yield put(setAiModelId(data));
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
        console.log(error);
    }
}

function* watchCloasing(): Generator {
    try {
        yield put(setIsShowModelDataModal(false));
        yield put(cleanModelData());
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
        console.log(error);
    }
}

function* watchSaving(): Generator {
    try {
        yield put(setIsProcessing(true));

        const modelData = (yield select(getModelData)) as string;
        const modelTestValue = (yield select(getTestValue)) as number;
        const schemaOrdering = (yield select(getSchemaOrdering)) as string;
        const guideStep = (yield select(getGuideStep)) as GuideStepEnum;
        const AiModelId = (yield select(getAiModelId)) as string;

        const data = {
            data: JSON.parse(modelData),
            testPercent: modelTestValue / 100,
            schemaId: AiModelId,
            schemaOrdering: schemaOrdering.split(",").map(x => x.trim())
        } as CreateModelData;
        const result = (yield call([DataProviderInstance, DataProviderInstance.create], data)) as RespondClient<string>;
        if (result.status !== 200) {
            if(typeof result.data === "string") {
                yield put(onThrowAlert({
                    message: result.data,
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                yield put(onThrowAlert({
                    message: JSON.stringify(result.data),
                    lifeTime: 10000
                } as NotificationModel));
            }
        }
        else {
            yield put(onThrowAlert({
                message: "Data successfully saved",
                lifeTime: 3000,
                type: NotificationModelType.Success
            } as NotificationModel));
            yield put(closeModelDataModalAction());
            yield put(loadOpenedSchema(AiModelId));
            if (guideStep !== GuideStepEnum.None) {
                yield put(setGuideStep(GuideStepEnum.OpenModelTab));
            }
        }
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
        console.log(error);
    }
    finally{
        yield put(setIsProcessing(false));
    }
}

function* watchCleaning({ payload: data }: PayloadAction<{ schemaId: string, cleanType: DatasetCleanupType }>): Generator {
    yield put(setIsDataCleaning(true));
    const result = (yield call([DataProviderInstance, DataProviderInstance.clean], data.schemaId, data.cleanType)) as RespondClient<string>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data,
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(closeModelDataModalAction());
        yield put(loadOpenedSchema(data.schemaId));
        yield put(onThrowAlert({
            message: "Data successfully cleaned",
            lifeTime: 3000,
            type: NotificationModelType.Warning
        } as NotificationModel));
    }
    yield put(setIsDataCleaning(false));
}
